import { fetchApi } from '../api/client';
import type { AppStatusPayload } from './app-status';

export interface CacheProgress {
  drivers: number;
  circuits: number;
  constructors: number;
  seasonsLoaded: number;
  totalSeasons: number;
}

export interface AppStatusResponse extends AppStatusPayload {
  fetchedAt?: string | null;
  warming?: boolean;
  progress?: CacheProgress;
}

export interface AppStatusSnapshot {
  status: AppStatusResponse | null;
  unknown: boolean;
}

interface LoadOptions {
  force?: boolean;
}

type Listener = () => void;
type FetchStatus = () => Promise<AppStatusResponse>;

const DEFAULT_SNAPSHOT: AppStatusSnapshot = {
  status: null,
  unknown: true,
};

async function defaultFetchStatus() {
  return fetchApi<AppStatusResponse>('status');
}

export function createAppStatusSource(fetchStatus: FetchStatus = defaultFetchStatus) {
  const listeners = new Set<Listener>();
  let snapshot = DEFAULT_SNAPSHOT;
  let inFlight: Promise<AppStatusResponse | null> | null = null;
  let requestId = 0;

  const emit = () => {
    listeners.forEach((listener) => listener());
  };

  const commitSnapshot = (next: AppStatusSnapshot, nextRequestId: number) => {
    if (nextRequestId !== requestId) {
      return;
    }
    snapshot = next;
    emit();
  };

  const runLoad = async (options: LoadOptions = {}) => {
    const { force = false } = options;

    if (!force && snapshot.status) {
      return snapshot.status;
    }

    if (!force && inFlight) {
      return inFlight;
    }

    requestId += 1;
    const nextRequestId = requestId;
    const loadPromise = fetchStatus()
      .then((status) => {
        commitSnapshot({
          status,
          unknown: false,
        }, nextRequestId);
        return status;
      })
      .catch((error) => {
        commitSnapshot({
          status: snapshot.status,
          unknown: false,
        }, nextRequestId);
        throw error;
      })
      .finally(() => {
        if (inFlight === loadPromise) {
          inFlight = null;
        }
      });

    inFlight = loadPromise;
    return loadPromise;
  };

  return {
    getSnapshot() {
      return snapshot;
    },
    subscribe(listener: Listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    load(options?: LoadOptions) {
      return runLoad(options);
    },
    reset() {
      snapshot = DEFAULT_SNAPSHOT;
      inFlight = null;
      requestId = 0;
      emit();
    },
  };
}

export const appStatusSource = createAppStatusSource();
